import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import BrandSetupEnd from "./BrandSetupEnd";
import BrandSetupStart from "./BrandSetupStart";
import SetBrandColors, { BoxStyleDefaults } from "./SetBrandColors";
import UploadYourLogo from "./UploadYourLogo";
import { enumBGAreas } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/BackgroundAreas";
import { handleImageSelect } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/LayoutControls";
import { GetAccountMetadata, UpdateAccountMetadata, UpdateChartMetadata } from "@/Utils/Promises";
import { GetDoc } from "@/Utils/StateLib";
import { Globals } from "@coreapp/CoreApp/Globals";
import { QBackgroundArea } from "@coreapp/CoreApp/QBackgroundArea";
import { QBackgroundMgr } from "@coreapp/CoreApp/QBackgroundMgr";
import { enumBGOption, enumSaveScope } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import type { UploadFileInfo } from "@coreapp/utils/DoUploadStatus";
import { Button, DialogActions, DialogTitle, Flex, Modal, Text } from "@theorgchartdev/storybook";
import { gBrandingDefaults } from "@coreapp/whitelabel/brand";
import { useCallback, useEffect, useState } from "react";
import { useTopic } from "@/hooks/useTopic";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { useToast, TOAST_STATUS } from "@/providers/ToastProvider";

export type BrandSetupStep = "intro" | "colors" | "background" | "complete";

type BrandSetupModalProps = {
  show: boolean;
  onClose: () => void;
};

const STEP_ORDER: BrandSetupStep[] = ["intro", "background", "colors", "complete"];

const BRAND_BACKGROUND_FILE = "logo-background.ocb";
const BRAND_BACKGROUND_DIR = "Masters";
const BRAND_BACKGROUND_AREA = "topLeft";

/** Replaces the layouts in one background area slot and commits the updated background. */
const applyLayoutToBackgroundArea = (
  backgroundMgr: QBackgroundMgr,
  area: string,
  layout: unknown
) => {
  const activeBackground = backgroundMgr.fActiveBackgroundArea?.Clone();
  const nextBackgroundArea = activeBackground?.GetAreas() ?? {};

  if (!nextBackgroundArea[area]) {
    const newArea = new QBackgroundArea();
    newArea.InitBackgroundArea();
    nextBackgroundArea[area] = newArea;
  }

  nextBackgroundArea[area].SetLayouts([layout]);
  backgroundMgr.UpdateActiveBackground(nextBackgroundArea);
};

/**
 * Links the brand background to the currently active chart — both in-memory (so the
 * background renders immediately) and in MongoDB (so it persists across reloads).
 * No-ops when no chart is loaded or when a background file is open in the editor.
 */
const linkBackgroundToActiveChart = (doc: ReturnType<typeof GetDoc>) => {
  const wsMgr = Globals._wsMgr;
  const docMgr = wsMgr?.GetDocMgr?.();
  const chartFileName = docMgr?.GetFileName?.(true);

  if (!chartFileName || docMgr?.IsBackground?.(chartFileName)) return;

  const iWizData = docMgr?.GetIWizDataInteractive?.();
  if (iWizData) {
    iWizData.SetMasterFile(BRAND_BACKGROUND_FILE);
    iWizData.SetMasterFolder(BRAND_BACKGROUND_DIR);
    iWizData.SetMasterOption(enumBGOption.linked);
  }

  const chartId = doc!.GetChartID?.();
  if (chartId) {
    UpdateChartMetadata(chartId, JSON.stringify({ mastertemplate: BRAND_BACKGROUND_FILE }));
  }
};

const createLogoBackground = (logoInfo: UploadFileInfo): Promise<boolean> => {
  return new Promise(resolve => {
    const wsMgr = Globals._wsMgr;
    const doc = GetDoc();
    const backgroundMgr = wsMgr?.fBackgroundMgr;
    if (!backgroundMgr || !doc || !logoInfo.directory || !logoInfo.fileName) {
      resolve(false);
      return;
    }

    handleImageSelect(
      logoInfo.directory,
      logoInfo.fileName,
      (layout: any) => {
        if (!layout) {
          resolve(false);
          return;
        }

        const pinArea = enumBGAreas[BRAND_BACKGROUND_AREA];
        layout.SetChartHorzCentering(pinArea.horz);
        layout.SetChartVertCentering(pinArea.vert);
        layout.SetPosition(0, 0);

        applyLayoutToBackgroundArea(backgroundMgr, BRAND_BACKGROUND_AREA, layout);

        // fPostSaveCallback is called by QXWSMgr.FileResult with server-confirmed success.
        // Same pattern used by DoSaveBackground throughout the codebase.
        wsMgr.fPostSaveCallback = (success: boolean) => {
          if (success) linkBackgroundToActiveChart(doc);
          resolve(success);
        };

        wsMgr.SaveDocument(
          BRAND_BACKGROUND_FILE,
          BRAND_BACKGROUND_DIR,
          false,
          enumSaveScope.background
        );
      },
      BRAND_BACKGROUND_AREA
    );
  });
};

// Inner component
export const BrandSetupModalContent = ({ show, onClose }: BrandSetupModalProps) => {
  const { addToast }: any = useToast();
  const orgInfo = Globals._wsMgr?.GetOrgInfo?.();
  const [currentStep, setCurrentStep] = useState<BrandSetupStep>("intro");
  const [isSaving, setIsSaving] = useState(false);
  const [boxStyleDraft, setBoxStyleDraft] = useState<BoxStyleDefaults>(gBrandingDefaults);
  const [uploadedLogo, setUploadedLogo] = useState<UploadFileInfo | null>(null);

  const stepIndex = STEP_ORDER.indexOf(currentStep);
  const setupSteps = STEP_ORDER.slice(1, -1);
  const setupStepIndex = setupSteps.indexOf(currentStep);
  const stepProgress =
    setupStepIndex > -1
      ? `${appstr("Step {current} of {total}", {
          current: setupStepIndex + 1,
          total: setupSteps.length
        })}`
      : undefined;

  useEffect(() => {
    if (show && orgInfo) {
      setCurrentStep("intro");
      const boxStyles = orgInfo.GetBoxStyleDefaults?.() ?? {};
      setBoxStyleDraft({
        brandingFill: boxStyles.brandingFill ?? gBrandingDefaults.brandingFill,
        brandingBorder: boxStyles.brandingBorder ?? gBrandingDefaults.brandingBorder
      });
    }
  }, [show, orgInfo]);

  const saveBrandingDefaults = async (): Promise<boolean> => {
    setIsSaving(true);

    try {
      let logoSaved = true;
      if (uploadedLogo) {
        logoSaved = await createLogoBackground(uploadedLogo);
        if (!logoSaved) {
          addToast({
            message: appstr("Logo could not be saved. Please try again or skip setup."),
            status: TOAST_STATUS.ERROR
          });
        }
      }

      const { result } = await GetAccountMetadata();
      if (!result) {
        throw new Error("GetAccountMetadata returned no result");
      }

      const currentTheme = result.theme;
      const currentBoxStyleDefaults = currentTheme.boxStyleDefaults ?? {};
      const payload = {
        onboardingNeeded: false,
        theme: {
          ...currentTheme,
          boxStyleDefaults: {
            ...currentBoxStyleDefaults,
            brandingFill: boxStyleDraft.brandingFill,
            brandingBorder: boxStyleDraft.brandingBorder
          }
        }
      };

      try {
        await UpdateAccountMetadata(JSON.stringify(payload));
      } catch {
        addToast({
          message: appstr("Brand colors could not be saved. Please try again or skip setup."),
          status: TOAST_STATUS.ERROR
        });
        return false;
      }

      // Reload system box styles with fresh branding token replacement
      Globals._wsMgr?.ReloadSystemBoxStyles?.();
      return logoSaved;
    } catch (e) {
      addToast({
        message: appstr("An error occurred while saving"),
        status: TOAST_STATUS.ERROR
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const goToNextStep = () => {
    const nextStep = STEP_ORDER[stepIndex + 1];
    if (nextStep) {
      setCurrentStep(nextStep);
      return;
    }
  };

  const goToPreviousStep = () => {
    const previousStep = STEP_ORDER[stepIndex - 1];
    if (previousStep) {
      setCurrentStep(previousStep);
      return;
    }
  };

  const handleSkip = async () => {
    await UpdateAccountMetadata(JSON.stringify({ onboardingNeeded: false }));
    onClose();
  };

  const handlePrimary = async () => {
    if (isSaving) {
      return;
    }

    if (currentStep === "intro") {
      goToNextStep();
      return;
    }

    if (currentStep === "background") {
      goToNextStep();
      return;
    }

    if (currentStep === "colors") {
      const saved = await saveBrandingDefaults();
      if (saved) goToNextStep();
      return;
    }

    onClose();
  };

  if (!show) {
    return null;
  }

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="brand-setup-modal"
        show={show}
        minWidth={"large"}
        maxWidth={"large"}
        centerScreen
        insertContentMargins={false}
        dataTestId="brand-setup-modal"
      >
        <DialogTitle divider>{appstr("Brand setup")}</DialogTitle>
        {currentStep === "intro" && <BrandSetupStart />}
        {currentStep === "background" && (
          <UploadYourLogo uploadedLogo={uploadedLogo} setUploadedLogo={setUploadedLogo} />
        )}
        {currentStep === "colors" && (
          <SetBrandColors boxStyleDraft={boxStyleDraft} setBoxStyleDraft={setBoxStyleDraft} />
        )}
        {currentStep === "complete" && <BrandSetupEnd />}
        <DialogActions divider={true}>
          <Flex alignItems="center" gap="8px" style={{ marginRight: "auto" }}>
            {setupStepIndex > 0 && (
              <Button
                onClick={goToPreviousStep}
                disabled={isSaving}
                iconOnly
                dataTestId="brand-setup-back-btn"
                iconProps={{
                  name: "arrow-left-solid"
                }}
                modifier="secondary"
              />
            )}
            {stepProgress && <Text size="small">{stepProgress}</Text>}
          </Flex>

          {(currentStep === "intro" ||
            currentStep === "background" ||
            currentStep === "colors") && (
            <DialogActions.Tertiary onClick={handleSkip} dataTestId="brand-setup-secondary-btn">
              {appstr("Skip setup")}
            </DialogActions.Tertiary>
          )}

          <DialogActions.Primary
            onClick={handlePrimary}
            disabled={isSaving}
            dataTestId="brand-setup-primary-btn"
          >
            {currentStep === "intro" && appstr("Get started")}
            {currentStep === "background" && appstr("Continue")}
            {currentStep === "colors" && appstr("Finish setup")}
            {currentStep === "complete" && appstr("Go to chart")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

type BrandSetupModalOuterProps = {
  onOnboardingComplete?: () => void;
};

// Outer component responsible for triggering the modal when onboarding is needed
const BrandSetupModal = ({ onOnboardingComplete }: BrandSetupModalOuterProps) => {
  const [show, setShow] = useState(false);
  const { isAdmin } = usePermissionCheck();

  const checkOnboarding = useCallback(() => {
    const wsMgr = Globals._wsMgr;
    if (wsMgr?.GetOrgInfo?.()?.GetOnboardingNeeded?.() && isAdmin && !wsMgr?.AdminHasNoCharts?.()) {
      setShow(true);
    }
  }, [isAdmin]);

  useEffect(() => {
    checkOnboarding();
  }, [checkOnboarding]);

  useTopic("LoginComplete", checkOnboarding);

  if (!show) {
    return null;
  }

  const onClose = () => {
    setShow(false);
    onOnboardingComplete?.();
  };

  return <BrandSetupModalContent show={show} onClose={onClose} />;
};

export default BrandSetupModal;
